import { Component, ElementRef, HostListener, ViewChild, inject } from "@angular/core";
import { Delegation, DelegationsService, PowerInfo } from "src/app/service/delegations.service";
import { HeaderComponent } from "../header/header.component";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { UserService } from "src/app/service/user.service";
import { ThemeService } from "src/app/service/theme.service";
import { ChatCacheService } from "src/app/service/chat-cache.service";
import { alertModal, alertModalDelete, extractErrorMessage } from "../helpers/alert";
import { NgClass } from "@angular/common";
import { base64ToFile, ImageCroppedEvent } from "ngx-image-cropper";
import { ImageCropperComponent } from "ngx-image-cropper";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { FINANCE_SUB_MODULES } from "src/app/constants/finance.constant";
import { VAULT_SUB_MODULES } from "src/app/constants/vault.constant";

@Component({
  selector: "app-create-profile",
  imports: [
    HeaderComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    ImageCropperComponent,
  ],
  templateUrl: "./create-profile.component.html",
  styleUrl: "./create-profile.component.css"
})
export class CreateProfileComponent {
  isMobileView: boolean = false;
  activeTab: 'create' | 'view' = 'create';
  signupForm: FormGroup;
  imagePreview: string | null = null;
  departments: any[] = [];
  showExportModal: boolean = false;
  selectedExportDepartment: string = '';
  isExportDeptDropdownOpen: boolean = false;
  isExporting: boolean = false;
  moduleOptions: { label: string; value: string }[] = [
    { label: "ARCHIVE", value: "ARCHIVE" },
    { label: "COLLECTIONS", value: "COLLECTIONS" },
    { label: "INVENTORY", value: "INVENTORY" },
    // { label: "CRM", value: "CRM" },
    { label: "LEAD-TRACKING", value: "LEAD-TRACKING" },
    { label: "CREATE-PROFILE", value: "CREATE-PROFILE" },
    // { label: "LINK-AI", value: "LINK-AI" },
    { label: "VAULT", value: "VAULT" },
    { label: "WHATSAPP-BROADCAST", value: "WHATSAPP-BROADCAST" },
    { label: "INTERACTIVE-TRACKER", value: "PROJECT-TRACKER" },
    // { label: "REIMBURSEMENT", value: "REIMBURSEMENT" }
    { label: "FINANCE", value: "FINANCE" }
  ];

  financeSubModulesList = [
    { label: "PROJECTS", value: FINANCE_SUB_MODULES.PROJECTS, description: "Dashboard, Projects, Invoices" },
    { label: "VENDORS", value: FINANCE_SUB_MODULES.VENDORS, description: "Vendors" },
    { label: "BUDGETS", value: FINANCE_SUB_MODULES.BUDGETS, description: "Budgets — the quarter sheets: HR head, HR and IT fill them, Accounts see them; Admins too only when ticked" }
  ];
  selectedFinanceSubModules: string[] = [];

  /** Vault's knowledge base: the tick that makes a project manager (or anyone who is not Admin, HR or a head) a curator. */
  vaultSubModulesList = [
    { label: "KNOWLEDGE BASE CURATOR", value: VAULT_SUB_MODULES.KNOWLEDGE_BASE, description: "Can create knowledge-base folders and add to shared ones" }
  ];
  selectedVaultSubModules: string[] = [];

  availableTags: string[] = [];
  tagSearchInput: string = '';
  showTagSuggestions: boolean = false;
  @ViewChild('tagInput') tagInputRef!: ElementRef<HTMLInputElement>;

  constructor(private fb: FormBuilder, private userService: UserService) {
    this.signupForm = this.fb.group({
      firstName: ["", Validators.required],
      lastName: [""],
      userName: ["", Validators.required],
      department: ["", Validators.required],
      officeLocation: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.required],
      modules: [[], Validators.required],
      employeeId: [""],
      isSSOCertified: [false],
      isEmailCreated: [false],
      isLaptopAdminPasswordSet: [false],
      accessTags: [[]],
    });
  }

  ngOnInit() {
    this.checkViewMode();
    this.showUserList();
    this.getDepartments();
    this.fetchAvailableTags();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkViewMode();
  }

  checkViewMode() {
    this.isMobileView = window.innerWidth <= 768;
  }

  imageChangedEvent: any = "";
  croppedImage: any = "";
  showCropper = false;
  selectedImage: File | null = null;
  private lastCroppedEvent?: ImageCroppedEvent;

  fileChangeEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file || !file.type.startsWith('image/')) {
      console.warn('Invalid file or not an image');
      return;
    }

    this.showCropper = false;
    this.imageCroppedReady = false;
    this.croppedImage = '';
    this.lastCroppedEvent = undefined;

    setTimeout(() => {
      this.imageChangedEvent = event;
      this.showCropper = true;
    }, 50);
  }



  imageCroppedReady = false;
  imageCropped(event: ImageCroppedEvent) {
    console.log('ImageCroppedEvent:', event);

    if (event.blob) {
      this.lastCroppedEvent = event;
      this.croppedImage = event.objectUrl;
      this.imageCroppedReady = true;
      console.log("Image cropped: using blob");
    } else {
      this.imageCroppedReady = false;
      console.warn(" Image cropped event triggered but no data.");
    }
  }

  // ── people administration (per founder, 20 Oct 2026) ───────────────────
  private async askReason(title: string, text: string, confirm: string, danger = false): Promise<string | null> {
    const r = await Swal.fire({
      title,
      text,
      input: 'text',
      inputPlaceholder: 'Why, in a line',
      inputAttributes: { maxlength: '300' },
      showCancelButton: true,
      confirmButtonText: confirm,
      confirmButtonColor: danger ? '#e53e3e' : undefined,
      inputValidator: (v: string) => (v && v.trim() ? null : 'A reason is needed'),
    });
    return r.isConfirmed ? String(r.value || '').trim() : null;
  }
  async onSuspendUser(user: any): Promise<void> {
    const reason = await this.askReason(`Suspend ${user.firstName}?`, 'They cannot log in and leave every list until unsuspended. The reason goes on the record.', 'Suspend', true);
    if (reason === null) return;
    this.userService.suspendUser(user._id, reason).subscribe({
      next: () => { alertModal('success', `${user.firstName} is suspended.`); this.showUserList(); },
      error: (err) => alertModal('error', extractErrorMessage(err, 'Could not suspend')),
    });
  }
  onUnsuspendUser(user: any): void {
    this.userService.unsuspendUser(user._id).subscribe({
      next: () => { alertModal('success', `${user.firstName} can log in again.`); this.showUserList(); },
      error: (err) => alertModal('error', extractErrorMessage(err, 'Could not unsuspend')),
    });
  }
  async onResetPassword(user: any): Promise<void> {
    const ask = await Swal.fire({
      title: `Reset ${user.firstName}'s password?`,
      text: 'You get a temporary password to hand over once. They must set their own at the next login.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Reset password',
      cancelButtonText: 'Cancel',
    });
    if (!ask.isConfirmed) return;
    this.userService.resetUserPassword(user._id).subscribe({
      next: (res: any) => {
        const temp = res?.data?.tempPassword || '';
        void Swal.fire({
          title: 'Temporary password',
          html: `<p>For <b>${user.userName}</b>. Shown once — copy it now.</p><p style="font-size:22px;letter-spacing:2px;font-family:monospace;user-select:all">${temp}</p><p style="color:#718096;font-size:13px">They must change it at their next login.</p>`,
          confirmButtonText: 'I have copied it',
        });
      },
      error: (err) => alertModal('error', extractErrorMessage(err, 'Could not reset the password')),
    });
  }
  async onAuditAccess(user: any): Promise<void> {
    const reason = await this.askReason(`View ${user.firstName}'s account?`, 'Read-only for one hour, and only what your own access opens; logged for every Admin. Your own session is replaced; log in again when you are done.', 'Open their account');
    if (reason === null) return;
    this.userService.auditAccess(user._id, reason).subscribe({
      next: (res: any) => {
        const d = res?.data;
        if (!d?.token) { alertModal('error', 'No session came back'); return; }
        const u = d.user || {};
        const keep: Record<string, string | null> = { fcm_token: localStorage.getItem('fcm_token'), browserUniqueId: localStorage.getItem('browserUniqueId') };
        // Start the person's view clean, as a logout does (2 Nov 2026): nothing of the Admin's own session may carry
        // over — no cached chat sign-in (the chat would act as the Admin), no cached lists.
        const savedThemes = ThemeService.preserveTheme();
        void ChatCacheService.clearAllUserCaches();
        localStorage.clear();
        ThemeService.restoreTheme(savedThemes);
        localStorage.setItem('guestToken', d.token);
        localStorage.setItem('guestID', u.userId || '');
        localStorage.setItem('username', u.userName || '');
        localStorage.setItem('department', u.department || '');
        localStorage.setItem('email', u.userEmail || '');
        localStorage.setItem('profileImg', u.profileImg || '');
        localStorage.setItem('isHead', String(u.isHead === true));
        localStorage.setItem('modules', Array.isArray(u.modules) ? u.modules.join(',') : '');
        localStorage.setItem('subModules', JSON.stringify(u.subModules || {}));
        localStorage.setItem('mustChangePassword', 'false');
        // The renewal clock starts now, so no renewal falls inside the hour (the server refuses one anyway).
        localStorage.setItem('loginTimestamp', String(Date.now()));
        for (const [k, v] of Object.entries(keep)) if (v) localStorage.setItem(k, v);
        window.location.href = '/#/home';
        window.location.reload();
      },
      error: (err) => alertModal('error', extractErrorMessage(err, 'Could not open the account')),
    });
  }
  async onDeleteUser(user: any): Promise<void> {
    const r = await Swal.fire({
      title: `Delete ${user.firstName}?`,
      input: 'radio',
      inputOptions: {
        '1': 'Delete the account; keep everything they did (records, files, chats)',
        '2': 'Delete the account and remove their one-to-one chats too (files stay)',
      },
      inputValue: '1',
      showCancelButton: true,
      confirmButtonText: 'Next',
      confirmButtonColor: '#e53e3e',
    });
    if (!r.isConfirmed) return;
    const level: 1 | 2 = Number(r.value) === 2 ? 2 : 1;
    const reason = await this.askReason('Why is the account deleted?', level === 2 ? 'Their one-to-one chats will be removed for good.' : 'Their records stay; only the login and the listings go.', 'Delete', true);
    if (reason === null) return;
    this.userService.deleteUserAccount(user._id, level, reason).subscribe({
      next: () => { alertModal('success', 'Account deleted.'); this.users = this.users.filter((x) => x._id !== user._id); this.totalUser = this.users.length; },
      error: (err) => alertModal('error', extractErrorMessage(err, 'Could not delete')),
    });
  }



  saveCrop() {
    console.log("Save clicked. Cropped image exists?", !!this.lastCroppedEvent?.blob);

    if (this.imageCroppedReady && this.lastCroppedEvent?.blob) {
      const blob = this.lastCroppedEvent.blob;

      // Preview using object URL
      this.imagePreview = URL.createObjectURL(blob);

      this.selectedImage = new File([blob], 'profile-image.jpg', {
        type: blob.type,
        lastModified: Date.now(),
      });

      this.showCropper = false;
      this.imageChangedEvent = '';
      this.imageCroppedReady = false;

      console.log("Saved successfully:", this.selectedImage);
    } else {
      console.warn(" Cropped image not ready yet. Please wait or try again.");
    }
  }



  imageLoaded() {
    console.log("Image loaded");
  }

  cropperReady() {
    console.log("Cropper ready");
  }

  loadImageFailed() {
    console.error("Load image failed");
  }

  cancelCrop() {
    this.showCropper = false;
    this.imageChangedEvent = "";
    this.croppedImage = "";
  }

  selectLocation(location: string) {
    this.signupForm.get("officeLocation")?.setValue(location);
  }
  selectDepartment(department: string) {
    const formatted = (department || '').toString().trim().toUpperCase() === 'EVENTS' ? 'OPERATIONS' : department;
    this.signupForm.get("department")?.setValue(formatted);
  }

  async onCreateDepartment() {
    const { value: deptName } = await Swal.fire({
      title: 'Create New Department',
      input: 'text',
      inputLabel: 'Department Name',
      inputPlaceholder: 'Enter department name...',
      showCancelButton: true,
      confirmButtonText: 'Add to List',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value) {
          return 'You need to write something!';
        }
      }
    });

    if (deptName) {
      const formattedName = deptName.trim().toUpperCase();
      // Add to local list if not already present
      const exists = this.departments.some(d =>
        (d.departmentName || d.name || d).toString().toUpperCase() === formattedName
      );

      if (!exists) {
        this.departments.push(formattedName);
      }

      this.selectDepartment(formattedName);
      Swal.fire({
        title: 'Department Ready',
        text: `"${formattedName}" added to dropdown. It will be created when you save the profile.`,
        icon: 'info',
        timer: 3000,
        toast: true,
        position: 'top-end',
        showConfirmButton: false
      });
    }
  }

  /** HR and the HR head may suspend / unsuspend people who are not Admins. */
  get isPeopleAdmin(): boolean {
    const d = (localStorage.getItem('department') || '').toUpperCase();
    return d === 'HR' || d === 'HR-HEAD';
  }
  get isAdmin(): boolean {
    const dept = (localStorage.getItem('department') || '').trim().toUpperCase();
    return ['ADMIN', 'DIRECTOR'].includes(dept);
  }

  /** Your own profile is open in the form: nobody changes their own Finance access, an Admin included (2 Nov 2026). */
  private get editingSelf(): boolean {
    return this.isEditMode && !!this.selectedUserId && this.selectedUserId === this.myUserId;
  }

  /** Finance (either section) can only be granted by an Admin, and never to themselves. */
  isFinanceSubModuleDisabled(subModule: string): boolean {
    return !this.isAdmin || this.editingSelf;
  }

  get financeLockReason(): string {
    return this.editingSelf ? 'Another Admin sets your own Finance access' : 'Only an Admin can give Finance access';
  }

  /** Finance is only listed for Admins; everyone else doesn't see it at all. */
  get visibleModuleOptions(): { label: string; value: string }[] {
    return this.isAdmin ? this.moduleOptions : this.moduleOptions.filter(m => m.value !== 'FINANCE');
  }

  isModuleDisabled(module: string): boolean {
    return module === 'FINANCE' && (!this.isAdmin || this.editingSelf);
  }

  toggleModule(module: string) {
    if (this.isModuleDisabled(module)) return;
    const modules = this.signupForm.get("modules")?.value || [];
    const index = modules.indexOf(module);
    if (index > -1) {
      modules.splice(index, 1);
      if (module === "FINANCE") {
        this.selectedFinanceSubModules = [];
      }
      if (module === "VAULT") {
        this.selectedVaultSubModules = [];
      }
    } else {
      modules.push(module);
      if (module === "FINANCE" && this.selectedFinanceSubModules.length === 0) {
        this.selectedFinanceSubModules = this.isAdmin
          ? [FINANCE_SUB_MODULES.PROJECTS, FINANCE_SUB_MODULES.VENDORS]
          : [FINANCE_SUB_MODULES.VENDORS];
      }
    }
    this.signupForm.get("modules")?.setValue([...modules]);
  }

  isFinanceSubModuleSelected(subModule: string): boolean {
    return this.selectedFinanceSubModules.includes(subModule);
  }

  toggleFinanceSubModule(subModule: string) {
    if (this.isFinanceSubModuleDisabled(subModule)) {
      return;
    }
    const idx = this.selectedFinanceSubModules.indexOf(subModule);
    if (idx > -1) {
      this.selectedFinanceSubModules.splice(idx, 1);
    } else {
      this.selectedFinanceSubModules.push(subModule);
    }

    const modules = this.signupForm.get("modules")?.value || [];
    if (this.selectedFinanceSubModules.length > 0 && !modules.includes("FINANCE")) {
      this.signupForm.get("modules")?.setValue([...modules, "FINANCE"]);
    } else if (this.selectedFinanceSubModules.length === 0 && modules.includes("FINANCE")) {
      this.signupForm.get("modules")?.setValue(modules.filter((m: string) => m !== "FINANCE"));
    }
  }

  isVaultSubModuleSelected(subModule: string): boolean {
    return this.selectedVaultSubModules.includes(subModule);
  }

  /** Ticking the curator right also ticks VAULT: the server keeps a sub-module only while its module is held. */
  toggleVaultSubModule(subModule: string) {
    const idx = this.selectedVaultSubModules.indexOf(subModule);
    if (idx > -1) {
      this.selectedVaultSubModules.splice(idx, 1);
    } else {
      this.selectedVaultSubModules.push(subModule);
    }
    const modules = this.signupForm.get("modules")?.value || [];
    if (this.selectedVaultSubModules.length > 0 && !modules.includes("VAULT")) {
      this.signupForm.get("modules")?.setValue([...modules, "VAULT"]);
    }
  }

  /** Everything the form holds about sub-modules, in the shape the server stores. */
  private subModulesPayload(modules: string[]): Record<string, string[]> {
    const payload: Record<string, string[]> = {};
    if (modules.includes("FINANCE") && this.selectedFinanceSubModules.length > 0) {
      payload["FINANCE"] = this.selectedFinanceSubModules;
    }
    if (modules.includes("VAULT") && this.selectedVaultSubModules.length > 0) {
      payload["VAULT"] = this.selectedVaultSubModules;
    }
    return payload;
  }

  fetchAvailableTags(search: string = '') {
    this.userService.getCommonTags('CREATE-PROFILE', search).subscribe({
      next: (res: any) => {
        const raw = res?.data || (Array.isArray(res) ? res : []);
        if (Array.isArray(raw)) {
          const list = raw.map((t: any) =>
            typeof t === 'string' ? t : (t?.name || t?.tag || '')
          ).filter(Boolean);

          if (search) {
            this.availableTags = Array.from(new Set([...this.availableTags, ...list]));
          } else {
            this.availableTags = Array.from(new Set(list));
          }
        }
      },
      error: (err) => {
        console.error('Failed to fetch tags:', err);
      }
    });
  }

  get accessTagsList(): string[] {
    return this.signupForm.get('accessTags')?.value || [];
  }

  get filteredTagSuggestions(): string[] {
    const current = this.accessTagsList;
    const query = (this.tagSearchInput || '').trim().toLowerCase();

    return this.availableTags.filter(tag => {
      const lower = tag.toLowerCase();
      const notSelected = !current.some((t: string) => t.toLowerCase() === lower);
      const matches = !query || lower.includes(query);
      return notSelected && matches;
    });
  }

  focusTagInput() {
    this.tagInputRef?.nativeElement?.focus();
  }

  onTagInputFocus() {
    this.showTagSuggestions = true;
    if (this.availableTags.length === 0) {
      this.fetchAvailableTags();
    }
    setTimeout(() => {
      const formCard = document.querySelector('.form-card');
      if (formCard) {
        formCard.scrollBy({
          top: 260,
          behavior: 'smooth'
        });
      }
    }, 120);
  }

  closeTagSuggestions(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.showTagSuggestions = false;
  }

  onTagInputBlur() {
    setTimeout(() => {
      // only blur if needed
    }, 200);
  }

  onTagSearchChange(event: any) {
    const val = (this.tagSearchInput || '').trim();
    if (val) {
      this.fetchAvailableTags(val);
    }
  }

  onTagKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const val = this.tagSearchInput.replace(/,/g, '').trim();
      if (val) {
        this.addAccessTag(val);
      }
    } else if (event.key === 'Backspace' && !this.tagSearchInput) {
      const current = this.accessTagsList;
      if (current.length > 0) {
        this.removeAccessTag(current.length - 1);
      }
    }
  }

  addAccessTag(tag: string) {
    const cleanTag = (tag || '').trim();
    if (!cleanTag) return;
    const current = this.accessTagsList;
    if (!current.some((t: string) => t.toLowerCase() === cleanTag.toLowerCase())) {
      const updated = [...current, cleanTag];
      this.signupForm.get('accessTags')?.setValue(updated);
      if (!this.availableTags.some(t => t.toLowerCase() === cleanTag.toLowerCase())) {
        this.availableTags.push(cleanTag);
      }
    }
    this.tagSearchInput = '';
  }

  selectTagSuggestion(tag: string) {
    this.addAccessTag(tag);
    this.focusTagInput();
  }

  removeAccessTag(index: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    const current = [...this.accessTagsList];
    if (index >= 0 && index < current.length) {
      current.splice(index, 1);
      this.signupForm.get('accessTags')?.setValue(current);
    }
  }

  selectIsoCertification(value: boolean) {
    this.signupForm.get('isSSOCertified')?.setValue(value);
  }

  selectIsEmailCreated(value: boolean) {
    this.signupForm.get('isEmailCreated')?.setValue(value);
  }

  selectIsLaptopAdminPasswordSet(value: boolean) {
    this.signupForm.get('isLaptopAdminPasswordSet')?.setValue(value);
  }

  isModuleSelected(module: string): boolean {
    const modules = this.signupForm.get("modules")?.value || [];
    return modules.includes(module);
  }

  showPassword: boolean = false;

  showToggleIcon: boolean = false;

  onPasswordInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.showToggleIcon = input.value.length > 0;
  }

  onSubmit(): void {
    // =========================
    // VALIDATION
    // =========================
    if (this.signupForm.invalid || (!this.selectedImage && !this.isEditMode)) {
      if (!this.selectedImage && !this.isEditMode) {
        alertModal("warning", "Please upload a profile image.");
      }

      this.signupForm.markAllAsTouched();
      return;
    }

    const modules = this.signupForm.get("modules")?.value || [];

    if (!Array.isArray(modules) || modules.length === 0) {
      alertModal("warning", "Please select at least one module.");
      return;
    }

    // =========================
    // CREATE FORMDATA
    // =========================
    const formData = new FormData();

    const firstName = this.signupForm.get("firstName")?.value || "";
    const lastName = this.signupForm.get("lastName")?.value || "";
    const userName = this.signupForm.get("userName")?.value || "";
    const email = this.signupForm.get("email")?.value || "";
    const department = this.signupForm.get("department")?.value || "";
    const officeLocation = this.signupForm.get("officeLocation")?.value || "";
    const password = this.signupForm.get("password")?.value || "";
    const employeeId = this.signupForm.get("employeeId")?.value || "";
    const isSSOCertified = this.signupForm.get("isSSOCertified")?.value;
    const isEmailCreated = this.signupForm.get("isEmailCreated")?.value;
    const isLaptopAdminPasswordSet = this.signupForm.get("isLaptopAdminPasswordSet")?.value;
    const accessTags = this.signupForm.get("accessTags")?.value || [];

    formData.append("firstName", firstName.trim());
    formData.append("lastName", lastName.trim());
    formData.append("userName", userName.trim());
    formData.append("email", email.trim());
    formData.append("department", department);
    formData.append("officeLocation", officeLocation);

    if (employeeId && employeeId.trim()) {
      formData.append("employeeId", employeeId.trim());
    }

    if (isSSOCertified !== undefined && isSSOCertified !== null && isSSOCertified !== "") {
      const isSSOBool = (isSSOCertified === true || isSSOCertified === 'true');
      formData.append("isSSOCertified", isSSOBool as any);
    }

    if (isEmailCreated !== undefined && isEmailCreated !== null && isEmailCreated !== "") {
      const isEmailCreatedBool = (isEmailCreated === true || isEmailCreated === 'true');
      formData.append("isEmailCreated", isEmailCreatedBool as any);
    }

    if (isLaptopAdminPasswordSet !== undefined && isLaptopAdminPasswordSet !== null && isLaptopAdminPasswordSet !== "") {
      const isLaptopAdminBool = (isLaptopAdminPasswordSet === true || isLaptopAdminPasswordSet === 'true');
      formData.append("isLaptopAdminPasswordSet", isLaptopAdminBool as any);
    }

    modules.forEach((module: string, index: number) => {
      formData.append(`modules[${index}]`, module);
    });

    formData.append("subModules", JSON.stringify(this.subModulesPayload(modules)));

    if (Array.isArray(accessTags)) {
      accessTags.forEach((tag: string, index: number) => {
        formData.append(`accessTags[${index}]`, tag);
      });
    }

    // Password
    if (password) {
      formData.append("password", password);
    }

    // Image
    if (this.selectedImage) {
      formData.append("file", this.selectedImage);
    }

    // Debug logs
    console.log("FormData entries:");
    formData.forEach((value, key) => {
      console.log(`${key}:`, value);
    });

    // =========================
    // EDIT MODE
    // =========================
    if (this.isEditMode && this.selectedUserId) {
      formData.append("userId", this.selectedUserId);

      this.userService.editUserProfile(formData).subscribe({
        next: (response: any) => {
          alertModal("success", "User profile updated successfully!");

          const index = this.users.findIndex(
            user => user._id === this.selectedUserId
          );

          if (index !== -1) {
            this.users.splice(index, 1);
          }

          const updatedUser = {
            _id: this.selectedUserId,
            ...this.signupForm.value,
            modules: modules, // clean modules
            subModules: this.subModulesPayload(modules),
            accessTags: accessTags,
            employeeId: employeeId.trim(),
            isSSOCertified: (isSSOCertified === true || isSSOCertified === 'true'),
            isEmailCreated: (isEmailCreated === true || isEmailCreated === 'true'),
            isLaptopAdminPasswordSet: (isLaptopAdminPasswordSet === true || isLaptopAdminPasswordSet === 'true'),
            profileImg: this.imagePreview
          };

          this.users.unshift(updatedUser);
          this.totalUser = this.users.length;

          this.resetForm();
        },
        error: (error) => {
          console.error("Update failed:", error);
          const errorMsg = extractErrorMessage(error, "Failed to update user profile.");
          alertModal("error", errorMsg);
        }
      });

    } else {
      // =========================
      // CREATE MODE
      // =========================
      this.userService.signUp(formData).subscribe({
        next: (response: any) => {
          alertModal("success", "User profile created successfully!");
          this.showUserList();
          this.getDepartments();
          this.resetForm();
        },
        error: (error) => {
          console.error("Signup failed:", error);
          const errorMsg = extractErrorMessage(error, "Signup failed. Please try again.");
          alertModal("error", errorMsg);
        }
      });
    }
  }




  users: any[] = [];
  /** Lists suspended people too (Admin, HR, HR-HEAD). */
  showSuspended = false;
  /** The "⋯" menu on a user row: which user it is open for and where it sits (fixed, so the scrolling list cannot clip it). */
  openUserMenu: string | null = null;
  menuPos = { top: 0, left: 0 };
  get menuUser(): any {
    return this.openUserMenu ? this.users.find((u) => u._id === this.openUserMenu) : null;
  }
  toggleUserMenu(id: string, ev: Event): void {
    ev.stopPropagation();
    if (this.openUserMenu === id) { this.openUserMenu = null; return; }
    const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    const width = 220;
    const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8));
    const top = r.bottom + 6 + 230 > window.innerHeight ? r.top - 6 - 230 : r.bottom + 6;
    this.menuPos = { top, left };
    this.openUserMenu = id;
  }
  closeUserMenu(): void {
    this.openUserMenu = null;
  }
  @HostListener('document:click')
  onDocumentClickCloseMenu(): void {
    if (this.openUserMenu) this.openUserMenu = null;
  }
  @HostListener('document:keydown.escape')
  onEscapeCloseMenu(): void {
    if (this.openUserMenu) this.openUserMenu = null;
  }
  totalUser = 0;
  searchQuery = '';
  selectedDepartmentFilter: string = '';

  getDeptName(dept: any): string {
    return typeof dept === 'string' ? dept : (dept?.departmentName || dept?.name || '');
  }

  filterByDepartment(dept: string) {
    this.selectedDepartmentFilter = dept || '';
    this.showUserList();
  }

  /** Only an Admin can put someone in the ADMIN department. */
  get selectableDepartments(): any[] {
    return this.isAdmin ? this.departments : this.departments.filter(d => this.getDeptName(d).toUpperCase() !== 'ADMIN');
  }

  getDepartments() {
    this.userService.getDepartmentUsers().subscribe({
      next: (res: any) => {
        const raw = res.data || [];
        const depts: any[] = [];
        const seen = new Set<string>();

        raw.forEach((d: any) => {
          let name = (typeof d === 'string' ? d : (d.departmentName || d.name || '')).toString().trim();
          if (name.toUpperCase() === 'EVENTS') {
            name = 'OPERATIONS';
          }
          if (name && !seen.has(name.toUpperCase())) {
            seen.add(name.toUpperCase());
            if (typeof d === 'string') {
              depts.push(name);
            } else {
              depts.push({ ...d, departmentName: name, name: name });
            }
          }
        });
        this.departments = depts;
      },
      error: (err: any) => {
        console.error('Failed to fetch departments:', err);
      }
    });
  }


  showUserList() {
    this.userService.getAllUsers(this.searchQuery, this.selectedDepartmentFilter, this.showSuspended).subscribe({
      next: (res: any) => {
        const raw = res.data || [];
        this.users = raw.map((u: any) => ({
          ...u,
          department: (u.department || '').toString().toUpperCase() === 'EVENTS' ? 'OPERATIONS' : u.department
        }));
        this.totalUser = this.users.length;
        console.log('User List', res);
      },
      error: (err: any) => {
        console.error('Failed to fetch user list:', err);
      }
    });
  }

  onSearchChange(value: string) {
    this.searchQuery = value;
    this.showUserList();
  }


  clearSearch() {
    this.searchQuery = '';
    this.showUserList();
  }


  selectedUserId: string | null = null;
  isEditMode = false;

  onEditUser(user: any) {
    const dept = (user.department || '').toString().toUpperCase() === 'EVENTS' ? 'OPERATIONS' : user.department;

    let userTags: string[] = [];
    if (user.accessTags) {
      if (Array.isArray(user.accessTags)) {
        userTags = user.accessTags.map((t: any) => typeof t === 'string' ? t : (t.name || t.tag || '')).filter(Boolean);
      } else if (typeof user.accessTags === 'string') {
        userTags = user.accessTags.split(',').map((t: string) => t.trim()).filter(Boolean);
      }
    }

    let isSSO = false;
    if (user.isSSOCertified === true || user.isSSOCertified === 'true') {
      isSSO = true;
    } else if (user.isSSOCertified === false || user.isSSOCertified === 'false') {
      isSSO = false;
    }

    let isEmailCreated = false;
    if (user.isEmailCreated === true || user.isEmailCreated === 'true') {
      isEmailCreated = true;
    } else if (user.isEmailCreated === false || user.isEmailCreated === 'false') {
      isEmailCreated = false;
    }

    let isLaptopAdminPassword = false;
    if (user.isLaptopAdminPasswordSet === true || user.isLaptopAdminPasswordSet === 'true') {
      isLaptopAdminPassword = true;
    } else if (user.isLaptopAdminPasswordSet === false || user.isLaptopAdminPasswordSet === 'false') {
      isLaptopAdminPassword = false;
    }

    const userModules: string[] = user.modules
      ? (Array.isArray(user.modules) ? user.modules : user.modules.split(','))
      : [];

    this.selectedFinanceSubModules = [];
    this.selectedVaultSubModules = [];
    if (user.subModules) {
      let parsed: any = null;
      if (typeof user.subModules === 'string') {
        try {
          parsed = JSON.parse(user.subModules);
        } catch (e) {
          parsed = null;
        }
      } else if (typeof user.subModules === 'object') {
        parsed = user.subModules;
      }

      if (parsed && Array.isArray(parsed.VAULT)) {
        this.selectedVaultSubModules = parsed.VAULT.map((s: any) => String(s).trim().toUpperCase());
      }
      if (parsed && Array.isArray(parsed.FINANCE)) {
        this.selectedFinanceSubModules = parsed.FINANCE.map((s: any) => String(s).trim().toUpperCase());
      } else if (Array.isArray(user.subModules)) {
        this.selectedFinanceSubModules = user.subModules.map((s: any) => String(s).trim().toUpperCase());
      }
    }

    if (userModules.includes("FINANCE") && this.selectedFinanceSubModules.length === 0) {
      this.selectedFinanceSubModules = this.isAdmin
        ? [FINANCE_SUB_MODULES.PROJECTS, FINANCE_SUB_MODULES.VENDORS]
        : [FINANCE_SUB_MODULES.VENDORS];
    }

    this.signupForm.patchValue({
      firstName: user.firstName,
      lastName: user.lastName || " ",
      userName: user.userName,
      email: user.email,
      password: '',
      department: dept,
      officeLocation: user.officeLocation,
      modules: userModules,
      employeeId: user.employeeId || '',
      isSSOCertified: isSSO,
      isEmailCreated: isEmailCreated,
      isLaptopAdminPasswordSet: isLaptopAdminPassword,
      accessTags: userTags,
    });

    this.tagSearchInput = '';
    this.showTagSuggestions = false;

    this.imagePreview = user.profileImg || null;
    this.selectedImage = null;

    this.signupForm.get('password')?.clearValidators();
    this.signupForm.get('password')?.updateValueAndValidity();

    this.isEditMode = true;
    this.selectedUserId = user._id;
    this.activeTab = 'create';
  }


  getFormattedModules(userOrModules: any, subModules?: any): string {
    if (!userOrModules) return '';
    let arr: string[] = [];
    let subObj: any = subModules;

    if (typeof userOrModules === 'object' && !Array.isArray(userOrModules) && userOrModules.modules) {
      arr = Array.isArray(userOrModules.modules) ? userOrModules.modules : userOrModules.modules.split(',');
      subObj = userOrModules.subModules;
    } else if (Array.isArray(userOrModules)) {
      arr = userOrModules;
    } else if (typeof userOrModules === 'string') {
      arr = userOrModules.split(',').map(m => m.trim());
    } else {
      return '';
    }

    let financeSubs: string[] = [];
    if (subObj) {
      try {
        const parsed = typeof subObj === 'string' ? JSON.parse(subObj) : subObj;
        if (parsed && Array.isArray(parsed.FINANCE)) {
          financeSubs = parsed.FINANCE;
        }
      } catch (e) { }
    }

    // Map each value to its label
    const mapped = arr.map(m => {
      const cleanM = (m || '').trim().toUpperCase();
      if (cleanM === 'FINANCE' && financeSubs.length > 0) {
        return `FINANCE (${financeSubs.join(', ')})`;
      }
      const option = this.moduleOptions.find(opt => opt.value === cleanM || opt.value === m);
      return option ? option.label : m;
    });

    return mapped.join(', ');
  }

  resetForm() {
    this.signupForm.reset({
      firstName: '',
      lastName: '',
      userName: '',
      email: '',
      password: '',
      department: '',
      officeLocation: '',
      modules: [],
      employeeId: '',
      isSSOCertified: false,
      isEmailCreated: false,
      isLaptopAdminPasswordSet: false,
      accessTags: [],
    });
    this.signupForm.get("modules")?.setValue([]);
    this.signupForm.get("accessTags")?.setValue([]);
    this.selectedFinanceSubModules = [];
    this.selectedVaultSubModules = [];
    this.tagSearchInput = '';
    this.showTagSuggestions = false;

    this.signupForm.get("password")?.setValidators(Validators.required);
    this.signupForm.get("password")?.updateValueAndValidity();

    this.selectedImage = null;
    this.imagePreview = null;
    this.isEditMode = false;
    this.selectedUserId = null;
  }

  // ── Excel Export ──────────────────────────────────────────────────────────

  openExportModal(): void {
    this.selectedExportDepartment = this.selectedDepartmentFilter || '';
    this.isExportDeptDropdownOpen = false;
    this.showExportModal = true;
  }

  closeExportModal(): void {
    if (this.isExporting) return;
    this.isExportDeptDropdownOpen = false;
    this.showExportModal = false;
  }

  toggleExportDeptDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isExporting) return;
    this.isExportDeptDropdownOpen = !this.isExportDeptDropdownOpen;
  }

  selectExportDept(deptName: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedExportDepartment = deptName;
    this.isExportDeptDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentGlobalClick(): void {
    this.isExportDeptDropdownOpen = false;
  }

  exportUsersToExcel(): void {
    this.isExporting = true;
    const deptFilter = this.selectedExportDepartment ? this.selectedExportDepartment.trim() : '';

    this.userService.getAllUsers('', deptFilter).subscribe({
      next: (res: any) => {
        const raw = res?.data || (Array.isArray(res) ? res : []);
        if (!raw || raw.length === 0) {
          this.isExporting = false;
          Swal.fire({
            icon: 'info',
            title: 'No Users Found',
            text: deptFilter
              ? `No users found for the "${deptFilter}" department.`
              : 'No users found to export.',
            confirmButtonColor: '#6366f1'
          });
          return;
        }

        try {
          const rows = raw.map((u: any, idx: number) => {
            const deptName = (u.department || '').toString().toUpperCase() === 'EVENTS'
              ? 'OPERATIONS'
              : (u.department || 'N/A');

            let tags = 'None';
            if (u.accessTags) {
              if (Array.isArray(u.accessTags)) {
                tags = u.accessTags
                  .map((t: any) => typeof t === 'string' ? t : (t?.name || t?.tag || ''))
                  .filter(Boolean)
                  .join(', ');
              } else if (typeof u.accessTags === 'string' && u.accessTags.trim()) {
                tags = u.accessTags;
              }
            }

            const isSSO = (u.isSSOCertified === true || u.isSSOCertified === 'true') ? 'Yes' : 'No';
            const isEmail = (u.isEmailCreated === true || u.isEmailCreated === 'true') ? 'Yes' : 'No';
            const isLaptopPwd = (u.isLaptopAdminPasswordSet === true || u.isLaptopAdminPasswordSet === 'true') ? 'Yes' : 'No';

            let createdDate = 'N/A';
            if (u.createdAt) {
              try {
                createdDate = new Date(u.createdAt).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                });
              } catch (e) {
                createdDate = u.createdAt;
              }
            }

            return {
              'S.No.': idx + 1,
              'Employee ID': u.employeeId || 'N/A',
              'Full Name': `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'N/A',
              'First Name': u.firstName || '',
              'Last Name': u.lastName || '',
              'Username': u.userName || 'N/A',
              'Email Address': u.email || 'N/A',
              'Department': deptName,
              'Office Location': u.officeLocation || 'N/A',
              'Assigned Modules': this.getFormattedModules(u) || 'None',
              'Access Control Tags': tags || 'None',
              'SSO Certified': isSSO,
              'Email Created': isEmail,
              'Laptop Admin Password Set': isLaptopPwd,
              'Created Date': createdDate
            };
          });

          const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows);

          // Auto-adjust column widths
          const colKeys = Object.keys(rows[0] || {});
          ws['!cols'] = colKeys.map(key => {
            const maxLen = Math.max(
              key.length,
              ...rows.map(r => String((r as any)[key] || '').length)
            );
            return { wch: Math.min(Math.max(maxLen + 3, 10), 50) };
          });

          const wb: XLSX.WorkBook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Users');

          const deptSlug = (deptFilter || 'All_Departments').replace(/[^a-zA-Z0-9_-]/g, '_');
          const dateStr = new Date().toISOString().slice(0, 10);
          const fileName = `Users_${deptSlug}_${dateStr}.xlsx`;

          const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([wbout], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          saveAs(blob, fileName);

          this.isExporting = false;
          this.showExportModal = false;

          Swal.fire({
            icon: 'success',
            title: 'Export Completed',
            text: `Successfully exported ${raw.length} user record${raw.length === 1 ? '' : 's'} to ${fileName}`,
            timer: 3500,
            toast: true,
            position: 'top-end',
            showConfirmButton: false
          });
        } catch (exportErr) {
          console.error('Error generating Excel file:', exportErr);
          this.isExporting = false;
          Swal.fire({
            icon: 'error',
            title: 'Export Failed',
            text: 'An error occurred while generating the Excel spreadsheet.',
            confirmButtonColor: '#ef4444'
          });
        }
      },
      error: (err: any) => {
        console.error('Failed to fetch users for export:', err);
        this.isExporting = false;
        const errorMsg = extractErrorMessage(err, 'Failed to fetch user profiles. Please try again.');
        Swal.fire({
          icon: 'error',
          title: 'Export Failed',
          text: errorMsg,
          confirmButtonColor: '#ef4444'
        });
      }
    });
  }



  // ───────────────────────── Manage Access (bulk, grouped by department) ─────────────────────────
  /** 'profiles' = the existing create/edit + list screens; 'access' = the matrix. */
  viewMode: 'profiles' | 'access' | 'flows' | 'history' | 'acting' = 'profiles';
  accessUsers: any[] = [];
  accessGroups: { dept: string; users: any[]; financeCount: number; changedCount: number }[] = [];
  accessDepartments: string[] = [];
  accessLoading = false;
  accessSaving = false;
  accessSearch = '';
  accessDeptFilter = '';
  accessOnlyChanged = false;
  collapsedDepts = new Set<string>();
  /** userId → edited copy of { modules, subModules }; absent = unchanged. */
  pendingAccess = new Map<string, { modules: string[]; subModules: Record<string, string[]> }>();
  accessErrors = new Map<string, string>();
  /** Only an Admin may grant or remove these (both Finance sections included). */
  readonly criticalModules = ['FINANCE'];
  readonly accessModuleColumns: { value: string; short: string }[] = [
    { value: 'ARCHIVE', short: 'Archive' },
    { value: 'COLLECTIONS', short: 'Collections' },
    { value: 'INVENTORY', short: 'Inventory' },
    { value: 'LEAD-TRACKING', short: 'Lead Track' },
    { value: 'VAULT', short: 'Vault' },
    { value: 'WHATSAPP-BROADCAST', short: 'WhatsApp' },
    { value: 'PROJECT-TRACKER', short: 'Interactive' },
    { value: 'CREATE-PROFILE', short: 'Create Profile' },
    { value: 'FINANCE', short: 'Finance' },
  ];

  private get myUserId(): string {
    return localStorage.getItem('guestID') || '';
  }

  openAccessView(): void {
    this.viewMode = 'access';
    if (!this.accessUsers.length) this.loadAccessUsers();
  }

  // ── Access history (Admin; 11 Oct 2026) ─────────────────────────────────────
  // Every access change the server applied (Manage Access, the profile edit form, new users), newest first.
  histRows: any[] = [];
  histTotal = 0;
  histPage = 1;
  histLoading = false;
  histSearch = '';
  histFinanceOnly = false;
  histFrom = '';
  histTo = '';
  private histTimer: any;

  // ───────────────────────── Acting for (delegated powers; Admin; 14 Oct 2026) ─────────────────────────
  private readonly delegations = inject(DelegationsService);
  actRows: Delegation[] = [];
  actLoading = false;
  actCatalogue: PowerInfo[] = [];
  actUsers: any[] = [];
  actUserSearch = '';
  actPicked: any = null;
  actForm: { powers: string[]; actsForUserId: string; endsOn: string; note: string } = { powers: [], actsForUserId: '', endsOn: '', note: '' };
  actSaving = false;
  actError = '';
  openActingView(): void {
    if (!this.isAdmin) return;
    this.viewMode = 'acting';
    if (!this.actCatalogue.length) this.delegations.catalogue().then((c) => (this.actCatalogue = c)).catch(() => {});
    if (!this.actUsers.length) this.userService.getAllUsers('', '').subscribe({ next: (res: any) => { this.actUsers = (res?.data || res || []).filter((u: any) => !u.revoked); }, error: () => {} });
    this.loadActing();
  }
  loadActing(): void {
    this.actLoading = true;
    this.delegations.list().then((rows) => (this.actRows = rows)).catch((e) => alertModal('error', extractErrorMessage(e, 'Could not load the grants'))).finally(() => (this.actLoading = false));
  }
  get actModules(): { module: string; powers: PowerInfo[] }[] {
    const map = new Map<string, PowerInfo[]>();
    for (const p of this.actCatalogue) map.set(p.module, [...(map.get(p.module) || []), p]);
    return [...map.entries()].map(([module, powers]) => ({ module, powers }));
  }
  get actAdmins(): any[] { return this.actUsers.filter((u) => String(u.department || '').toUpperCase() === 'ADMIN' && (!this.actPicked || String(u._id) !== String(this.actPicked._id))); }
  get actFilteredUsers(): any[] {
    const q = this.actUserSearch.trim().toLowerCase();
    if (!q || this.actPicked) return [];
    // Anyone can hold powers, including people in the Admin department (Yashvi giving tasks in Manish's name is the first use); only the person themselves is left out.
    const me = localStorage.getItem('guestID') || '';
    return this.actUsers.filter((u) => String(u._id) !== me && `${u.firstName || ''} ${u.lastName || ''} ${u.userName || ''} ${u.department || ''}`.toLowerCase().includes(q)).slice(0, 8);
  }
  actName(u: any): string { return `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.userName || ''; }
  pickActUser(u: any): void { this.actPicked = u; this.actUserSearch = this.actName(u); }
  clearActUser(): void { this.actPicked = null; this.actUserSearch = ''; }
  togglePower(key: string): void { this.actForm.powers = this.actForm.powers.includes(key) ? this.actForm.powers.filter((k) => k !== key) : [...this.actForm.powers, key]; }
  get actCanSave(): boolean { return !!this.actPicked && this.actForm.powers.length > 0 && !this.actSaving; }
  powerLabel(key: string): string { return this.actCatalogue.find((p) => p.key === key)?.label || key; }
  async saveGrant(): Promise<void> {
    if (!this.actCanSave) return;
    this.actSaving = true;
    this.actError = '';
    try {
      await this.delegations.grant({ userId: String(this.actPicked._id), powers: this.actForm.powers, actsForUserId: this.actForm.actsForUserId || undefined, endsOn: this.actForm.endsOn || null, note: this.actForm.note });
      this.actForm = { powers: [], actsForUserId: '', endsOn: '', note: '' };
      this.clearActUser();
      this.loadActing();
    } catch (e) {
      this.actError = extractErrorMessage(e, 'Could not save the grant');
    } finally {
      this.actSaving = false;
    }
  }
  async revokeGrant(row: Delegation): Promise<void> {
    if (!confirm(`Take back ${row.delegate.name}'s powers (${row.powers.map((p) => this.powerLabel(p)).join(', ')})?`)) return;
    try {
      await this.delegations.revoke(row.id, '');
      this.loadActing();
    } catch (e) {
      alertModal('error', extractErrorMessage(e, 'Could not revoke'));
    }
  }
  actWhen(iso: string | null): string { return iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''; }

  openHistoryView(): void {
    if (!this.isAdmin) return;
    this.viewMode = 'history';
    if (!this.histRows.length) this.loadHistory(true);
  }
  loadHistory(reset = false): void {
    if (reset) { this.histPage = 1; this.histRows = []; }
    this.histLoading = true;
    this.userService.getAccessLogs({ search: this.histSearch, financeOnly: this.histFinanceOnly, from: this.histFrom, to: this.histTo, page: this.histPage, limit: 50 }).subscribe({
      next: (res: any) => {
        const d = res?.data || {};
        const items = Array.isArray(d) ? d : (d.items || []);
        this.histRows = reset ? items : [...this.histRows, ...items];
        this.histTotal = Array.isArray(d) ? items.length : (d.total || items.length);
        this.histLoading = false;
      },
      error: () => { this.histLoading = false; alertModal('error', 'Could not load the access history'); },
    });
  }
  onHistFilterChange(): void {
    clearTimeout(this.histTimer);
    this.histTimer = setTimeout(() => this.loadHistory(true), 350);
  }
  loadMoreHistory(): void { this.histPage += 1; this.loadHistory(false); }
  get histHasMore(): boolean { return this.histRows.length < this.histTotal; }
  /** One readable line per change: +MODULE, −MODULE, Finance → +PROJECTS / −VENDORS. */
  histChanges(row: any): { text: string; kind: 'add' | 'remove' | 'fin' }[] {
    if (row?.action && row.action !== 'access') {
      const label: Record<string, string> = {
        suspend: 'Suspended',
        unsuspend: 'Unsuspended',
        'password-reset': 'Password reset — temporary password issued',
        'audit-access': `Audit access for ${row.meta?.minutes || 60} min`,
        delete: `Deleted (${row.meta?.level === 2 ? 'chats removed too' : 'data kept'})`,
      };
      const kind: 'add' | 'remove' | 'fin' = row.action === 'unsuspend' ? 'add' : row.action === 'suspend' || row.action === 'delete' ? 'remove' : 'fin';
      return [{ text: `${label[row.action] || row.action}${row.reason ? ' — ' + row.reason : ''}`, kind }];
    }
    const out: { text: string; kind: 'add' | 'remove' | 'fin' }[] = [];
    const d = row?.diff || {};
    for (const m of d.added || []) out.push({ text: '+ ' + this.histLabel(m), kind: 'add' });
    for (const m of d.removed || []) out.push({ text: '− ' + this.histLabel(m), kind: 'remove' });
    const subs = d.subModules || {};
    for (const mod of Object.keys(subs)) {
      const a = (subs[mod]?.added || []).map((x: string) => '+ ' + this.histLabel(x));
      const r = (subs[mod]?.removed || []).map((x: string) => '− ' + this.histLabel(x));
      if (a.length || r.length) out.push({ text: `${this.histLabel(mod)} sections: ${[...a, ...r].join(', ')}`, kind: 'fin' });
    }
    if (!out.length) out.push({ text: 'no change in modules (saved as is)', kind: 'fin' });
    return out;
  }
  histLabel(key: string): string {
    const map: Record<string, string> = { FINANCE: 'Finance', 'CREATE-PROFILE': 'Create Profile', 'LEAD-TRACKING': 'Lead Tracking', 'PROJECT-TRACKER': 'Project Tracker', 'WHATSAPP-BROADCAST': 'WhatsApp Broadcast', 'LINK-AI': 'Link AI', PROJECTS: 'Projects', VENDORS: 'Vendors' };
    return map[key] || (key || '').charAt(0) + (key || '').slice(1).toLowerCase();
  }
  histSource(row: any): string {
    return row?.source === 'people-admin' ? 'People admin' : row?.source === 'edit-user-details' ? 'Edit profile form' : row?.source === 'create-new-user' ? 'New user' : 'Manage Access';
  }
  histWhen(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true });
  }
  histTouchesFinance(row: any): boolean {
    const d = row?.diff || {};
    return (d.added || []).includes('FINANCE') || (d.removed || []).includes('FINANCE') || !!d.subModules?.FINANCE;
  }

  // ── Approval flows (Admin; 11 Oct 2026) ─────────────────────────────────────
  // Vendor payments follow these flows. Accounts always pay at the end, so they are never a step.
  readonly FLOW_ROLES = [
    // The charged project's BD; their business heads may approve for them (founder, 2 Nov 2026). The server puts it
    // first on a project payment whose flow has none; a flow may place it later.
    { role: 'PROJECT_BD', label: 'Project BD (or their business head)' },
    { role: 'BUSINESS_HEAD', label: 'Business head' },
    { role: 'OPERATIONS', label: 'Operations' },
    { role: 'HR-HEAD', label: 'Procurement (HR-Head)' },
    { role: 'HR', label: 'HR (anyone in HR)' },
    { role: 'SALES', label: 'Sales / BD' },
    { role: 'ADMIN', label: 'Admin (Directors)' },
    { role: 'PROJECT_CREATOR', label: 'Project creator (whoever created the charged project)' },
  ];
  /** The Internal capex route has no project and no business head (server: NOT_FOR_INTERNAL_ROLES). */
  readonly INTERNAL_FLOW_ROLES = this.FLOW_ROLES.filter(r => !['BUSINESS_HEAD', 'PROJECT_BD', 'PROJECT_CREATOR'].includes(r.role));
  /** Its default Admin line (server: INTERNAL_ADMIN_STEP_ABOVE). */
  readonly INTERNAL_ADMIN_ABOVE_DEFAULT = 10000;
  isInternalFlow(f: any): boolean { return f?.kind === 'internal'; }
  get internalFlow(): any { return this.flows.find(f => this.isInternalFlow(f)) || null; }
  get pickableFlows(): any[] { return this.flows.filter(f => !this.isInternalFlow(f)); }
  inrAmount(n: any): string { return (Number(n) || 0).toLocaleString('en-IN'); }
  readonly FLOW_DEPTS = ['SALES', 'ASSISTANT-SALES', 'OPERATIONS', 'HR', 'HR-HEAD', 'ACCOUNTS', 'ADMIN', 'MARKETING', 'CS', 'DESIGNER', 'DEVELOPER', 'VIDEO-EDITOR', 'UNITY', '3D-DESIGN', '2D-DESIGN', 'TESTER', 'IT-ADMIN'];
  flows: any[] = [];
  flowsLoading = false;
  flowEdit: any = null;
  flowError = '';
  flowAddRole = '';

  openFlowsView(): void {
    if (!this.isAdmin) return;
    this.viewMode = 'flows';
    this.loadFlows();
  }
  loadFlows(): void {
    this.flowsLoading = true;
    this.userService.getApprovalFlows(true).subscribe({
      next: (res: any) => { this.flows = res?.data || []; this.flowsLoading = false; },
      error: (err: any) => { this.flowsLoading = false; alertModal('error', extractErrorMessage(err) || 'Could not load approval flows'); },
    });
  }
  flowRoleLabel(role: string): string { return this.FLOW_ROLES.find(r => r.role === role)?.label || role; }
  flowChain(f: any): string { return [...(f?.steps || []).map((s: any) => s.label || this.flowRoleLabel(s.role)), 'Accounts (pay)'].join(' → '); }
  editFlow(f?: any): void {
    this.flowError = '';
    this.flowAddRole = '';
    this.flowEdit = f
      ? { id: f.id, name: f.name, steps: (f.steps || []).map((s: any) => s.role), requesterDepartments: [...(f.requesterDepartments || [])], defaultFor: [...(f.defaultFor || [])], active: f.active !== false,
          ...(this.isInternalFlow(f) ? { kind: 'internal', adminAbove: f.adminAbove ?? this.INTERNAL_ADMIN_ABOVE_DEFAULT } : {}) }
      : { id: '', name: '', steps: [], requesterDepartments: [], defaultFor: [], active: true };
  }
  get flowRoleChoices(): { role: string; label: string }[] { return this.isInternalFlow(this.flowEdit) ? this.INTERNAL_FLOW_ROLES : this.FLOW_ROLES; }
  addFlowStep(): void { if (this.flowAddRole) { this.flowEdit.steps.push(this.flowAddRole); this.flowAddRole = ''; } }
  moveFlowStep(i: number, d: number): void {
    const s = this.flowEdit.steps, j = i + d;
    if (j < 0 || j >= s.length) return;
    [s[i], s[j]] = [s[j], s[i]];
  }
  removeFlowStep(i: number): void { this.flowEdit.steps.splice(i, 1); }
  toggleFlowDept(list: 'requesterDepartments' | 'defaultFor', d: string): void {
    const arr: string[] = this.flowEdit[list];
    const i = arr.indexOf(d);
    if (i >= 0) arr.splice(i, 1); else arr.push(d);
    if (list === 'requesterDepartments' && arr.length) this.flowEdit.defaultFor = this.flowEdit.defaultFor.filter((x: string) => arr.includes(x));
  }
  get flowDefaultChoices(): string[] { return this.flowEdit?.requesterDepartments?.length ? this.flowEdit.requesterDepartments : this.FLOW_DEPTS; }
  saveFlow(): void {
    const f = this.flowEdit;
    if (!f || (f.name || '').trim().length < 3) { this.flowError = 'Give the flow a name.'; return; }
    if (this.isInternalFlow(f)) {
      if (!f.steps.length) { this.flowError = 'The Internal capex route needs at least one step before Accounts pay.'; return; }
      if (!(Number(f.adminAbove) >= 0)) { this.flowError = 'Enter the amount above which an Admin approves too (₹0 or more).'; return; }
    }
    const body = this.isInternalFlow(f) ? { id: f.id, name: f.name.trim(), steps: f.steps, adminAbove: Number(f.adminAbove), active: true } : { ...f, name: f.name.trim() };
    this.userService.saveApprovalFlow(body).subscribe({
      next: () => { this.flowEdit = null; this.loadFlows(); alertModal('success', 'Approval flow saved'); },
      error: (err: any) => { this.flowError = extractErrorMessage(err) || 'Could not save the flow'; },
    });
  }
  removeFlow(f: any): void {
    if (!confirm(`Remove the approval flow "${f.name}"? Requests already in this flow keep their steps.`)) return;
    this.userService.deleteApprovalFlow(f.id).subscribe({
      next: () => this.loadFlows(),
      error: (err: any) => alertModal('error', extractErrorMessage(err) || 'Could not remove the flow'),
    });
  }

  openProfilesView(tab: 'create' | 'view' = 'create'): void {
    this.viewMode = 'profiles';
    this.activeTab = tab;
  }

  loadAccessUsers(): void {
    this.accessLoading = true;
    this.userService.getAllUsers('', '').subscribe({
      next: (res: any) => {
        const raw: any[] = res?.data || [];
        this.accessUsers = raw
          .map((u: any) => ({
            ...u,
            department: ((u.department || '').toString().toUpperCase() === 'EVENTS' ? 'OPERATIONS' : (u.department || '')).toString().toUpperCase() || '(NO DEPARTMENT)',
            modules: Array.isArray(u.modules) ? [...u.modules] : [],
            subModules: u.subModules && typeof u.subModules === 'object' ? { ...u.subModules } : {},
          }))
          .sort((a, b) => a.department.localeCompare(b.department) || `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
        this.accessDepartments = Array.from(new Set(this.accessUsers.map(u => u.department)));
        // ADMIN first, then alphabetical
        this.accessDepartments.sort((a, b) => (a === 'ADMIN' ? -1 : b === 'ADMIN' ? 1 : a.localeCompare(b)));
        this.pendingAccess.clear();
        this.accessErrors.clear();
        this.accessLoading = false;
        this.rebuildAccessGroups();
      },
      error: (err: any) => {
        this.accessLoading = false;
        alertModal('error', extractErrorMessage(err, 'Could not load users.'));
      },
    });
  }

  rebuildAccessGroups(): void {
    const q = this.accessSearch.trim().toLowerCase();
    const groups: typeof this.accessGroups = [];
    for (const dept of this.accessDepartments) {
      if (this.accessDeptFilter && dept !== this.accessDeptFilter) continue;
      const users = this.accessUsers.filter(u => {
        if (u.department !== dept) return false;
        if (this.accessOnlyChanged && !this.isAccessChanged(u)) return false;
        if (!q) return true;
        return `${u.firstName} ${u.lastName} ${u.userName} ${u.email}`.toLowerCase().includes(q);
      });
      if (!users.length) continue;
      groups.push({
        dept,
        users,
        financeCount: users.filter(u => this.hasAccessModule(u, 'FINANCE')).length,
        changedCount: users.filter(u => this.isAccessChanged(u)).length,
      });
    }
    this.accessGroups = groups;
  }

  toggleDept(dept: string): void {
    if (this.collapsedDepts.has(dept)) this.collapsedDepts.delete(dept);
    else this.collapsedDepts.add(dept);
  }

  /** Current (edited or original) access for a row. */
  private effectiveAccess(u: any): { modules: string[]; subModules: Record<string, string[]> } {
    return this.pendingAccess.get(u._id) || { modules: u.modules || [], subModules: u.subModules || {} };
  }

  hasAccessModule(u: any, module: string): boolean {
    return this.effectiveAccess(u).modules.includes(module);
  }

  hasFinanceSub(u: any, sub: string): boolean {
    const a = this.effectiveAccess(u);
    return a.modules.includes('FINANCE') && (a.subModules['FINANCE'] || []).includes(sub);
  }

  isAccessChanged(u: any): boolean {
    return this.pendingAccess.has(u._id);
  }

  get changedCount(): number {
    return this.pendingAccess.size;
  }

  /** Mirrors the server rules so locked cells are visibly locked, not silently rejected. */
  canEditUser(u: any): boolean {
    if (this.isAdmin) return true;
    if (u.department === 'ADMIN') return false;
    if (u._id === this.myUserId) return false;
    return true;
  }

  canEditModule(u: any, module: string): boolean {
    if (!this.canEditUser(u)) return false;
    if (this.criticalModules.includes(module) && !this.isAdmin) return false;
    if (this.criticalModules.includes(module) && u._id === this.myUserId) return false; // nobody gives themselves Finance (2 Nov 2026)
    if (module === 'CREATE-PROFILE' && this.isAdmin && u._id === this.myUserId) return false; // lock-out guard
    return true;
  }

  canEditFinanceSub(u: any, sub: string): boolean {
    if (!this.canEditModule(u, 'FINANCE')) return false;
    if (sub === 'PROJECTS' && !this.isAdmin) return false;
    return true;
  }

  lockReason(u: any, module: string): string {
    if (this.canEditUser(u) && this.canEditModule(u, module)) return '';
    if (!this.isAdmin && u.department === 'ADMIN') return 'Only an Admin can change an Admin user';
    if (u._id === this.myUserId && this.criticalModules.includes(module)) return 'Another Admin sets your own Finance access';
    if (u._id === this.myUserId) return module === 'CREATE-PROFILE' ? "You can't remove your own Create-Profile access" : "You can't change your own access";
    if (this.criticalModules.includes(module)) return 'Only an Admin can give or remove Finance access';
    return '';
  }

  private setPending(u: any, next: { modules: string[]; subModules: Record<string, string[]> }): void {
    const sameList = (a: string[] = [], b: string[] = []) => JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
    const origSub = (u.subModules && u.subModules['FINANCE']) || [];
    const nextSub = next.subModules['FINANCE'] || [];
    const unchanged = sameList(u.modules || [], next.modules) && sameList(origSub, nextSub);
    if (unchanged) this.pendingAccess.delete(u._id);
    else this.pendingAccess.set(u._id, next);
    this.accessErrors.delete(u._id);
    this.rebuildAccessGroups();
  }

  toggleAccessModule(u: any, module: string): void {
    if (!this.canEditModule(u, module)) return;
    const cur = this.effectiveAccess(u);
    let modules = [...cur.modules];
    const subModules: Record<string, string[]> = { ...cur.subModules };
    if (modules.includes(module)) {
      modules = modules.filter(m => m !== module);
      if (module === 'FINANCE') delete subModules['FINANCE'];
    } else {
      modules.push(module);
      if (module === 'FINANCE' && !(subModules['FINANCE'] || []).length) {
        subModules['FINANCE'] = this.isAdmin
          ? [FINANCE_SUB_MODULES.PROJECTS, FINANCE_SUB_MODULES.VENDORS]
          : [FINANCE_SUB_MODULES.VENDORS];
      }
    }
    this.setPending(u, { modules, subModules });
  }

  toggleAccessFinanceSub(u: any, sub: string): void {
    if (!this.canEditFinanceSub(u, sub)) return;
    const cur = this.effectiveAccess(u);
    let modules = [...cur.modules];
    const subModules: Record<string, string[]> = { ...cur.subModules };
    let fin = [...(subModules['FINANCE'] || [])];
    // An empty list with Finance on means "every ordinary section" server-side; spell them out before adding one.
    if (!fin.length && modules.includes('FINANCE')) fin = [FINANCE_SUB_MODULES.PROJECTS, FINANCE_SUB_MODULES.VENDORS];
    if (fin.includes(sub)) fin = fin.filter(s => s !== sub);
    else fin.push(sub);
    if (!fin.length) {
      // No sections left = no Finance at all (an empty list would mean "everything" server-side).
      modules = modules.filter(m => m !== 'FINANCE');
      delete subModules['FINANCE'];
    } else {
      if (!modules.includes('FINANCE')) modules.push('FINANCE');
      subModules['FINANCE'] = fin;
    }
    this.setPending(u, { modules, subModules });
  }

  discardAccessChanges(): void {
    this.pendingAccess.clear();
    this.accessErrors.clear();
    this.rebuildAccessGroups();
  }

  saveAccessChanges(): void {
    if (!this.pendingAccess.size || this.accessSaving) return;
    const changes = Array.from(this.pendingAccess.entries()).map(([userId, a]) => ({
      userId,
      modules: a.modules,
      subModules: a.subModules,
    }));
    this.accessSaving = true;
    this.userService.updateUserAccess(changes).subscribe({
      next: (res: any) => {
        const data = res?.data || {};
        const results: any[] = data.results || [];
        this.accessErrors.clear();
        for (const r of results) {
          const user = this.accessUsers.find(u => u._id === r.userId);
          if (!user) continue;
          if (r.ok) {
            user.modules = r.modules || user.modules;
            user.subModules = r.subModules || user.subModules;
            this.pendingAccess.delete(r.userId);
          } else {
            this.accessErrors.set(r.userId, r.message || 'Not saved');
          }
        }
        this.accessSaving = false;
        this.rebuildAccessGroups();
        this.showUserList();
        const failed = Number(data.failed || 0);
        const updated = Number(data.updated || 0);
        if (failed) {
          alertModal('error', `${updated} saved, ${failed} not saved. The rows that failed are marked in red.`);
        } else {
          alertModal('success', `Access updated for ${updated} user${updated === 1 ? '' : 's'}.`);
        }
      },
      error: (err: any) => {
        this.accessSaving = false;
        alertModal('error', extractErrorMessage(err, 'Could not save access changes.'));
      },
    });
  }
}
